import type { RootReducerState, CartSelection } from "../reducers";
import type { SellerGroupWithBaskets, Validation } from "types/Order";
import { BASKET_TYPE } from "constants/order";
import { efoodSelector, selectCurrentLangId, siteSelector } from "./site";
import { createSelector } from "@reduxjs/toolkit";
import {
  type MPSellerSelectionData,
  SellerType,
  type SubBasket,
  type SellerIdentifier,
} from "@efood-commerce-sdk/typescript-axios-transaction-efood";
import { selectOnlineSellers } from "./catalog";
import { createBasketKey } from "components/cart/CartList/CartBaskets.helper";

const EMPTY_ARRAY = [];

export type BasketValidationType = { key: string; validation: Validation };

export const orderSelector = (state: RootReducerState) => state.order;

export const orderIdSelector = (state: RootReducerState) => state.order.id;

export const orderApprovalsSelector = (state: RootReducerState) =>
  state.order.approvals ?? EMPTY_ARRAY;

export const promotionCodeSelector = (state: RootReducerState) =>
  state.order.promotionCode ?? EMPTY_ARRAY;

export const rewardsSelector = (state: RootReducerState) =>
  state.order.rewards ?? EMPTY_ARRAY;

export const orderTotalSelector = (state: RootReducerState) =>
  state.order.total;

export const orderTotalAdjustmentSelector = (state: RootReducerState) =>
  state.order.totalAdjustment;

export const orderTotalProductSelector = (state: RootReducerState) =>
  state.order.totalProduct;

export const orderTotalShippingSelector = (state: RootReducerState) =>
  state.order.totalShipping;

export const basketSwitchTimeExpiredSelector = (state: RootReducerState) =>
  state.order.switchTimeExpired;

export const numItemsSelector = (state: RootReducerState) =>
  state.order.numItems;

export const shipInfosSelector = (state: RootReducerState) =>
  state.order.shipInfos;

export const isFetchingSelector = (state: RootReducerState) =>
  state.order.isFetching;

export const selectAllBaskets = (state: RootReducerState) =>
  state.order.baskets ?? EMPTY_ARRAY;

export interface ExtendedSellerIdentifier extends SellerIdentifier {
  displayName?: string;
  image1?: string;
  image2?: string;
}

/**
 * Creating a new selector which groups the baskets by seller, because the openapi generator
 * does not support oneOf to allow hcl api be backwards compatible and support seller grouping.
 * Also this selector includes the seller displayName, images and seller totals in the result.
 */
export const selectGroupedBasketsBySeller = createSelector(
  [selectAllBaskets, siteSelector, selectCurrentLangId, selectOnlineSellers],
  (baskets, currentSite, langId, onlineSellers): SellerGroupWithBaskets[] => {
    const groups = new Map<string, SellerGroupWithBaskets>();

    for (const basket of baskets) {
      const seller = basket.identifier?.sellerIdentifier;
      if (!seller) continue;

      const { sellerType, identifier } = seller;
      const existing = groups.get(identifier);

      if (!existing) {
        // Fallback values
        let displayName: string | undefined;
        let image1: string | undefined;
        let image2: string | undefined;

        if (sellerType === SellerType.Internal) {
          const descEntry = currentSite?.description?.find(
            (desc) => desc.languageId === langId
          );
          displayName = descEntry?.displayName;
        } else if (sellerType === SellerType.External) {
          const match: MPSellerSelectionData | undefined = onlineSellers.find(
            (s) => s.extSellerId === identifier
          );
          if (match) {
            displayName = match.displayName;
            image1 = match.image1;
            image2 = match.image2;
          }
        }

        groups.set(identifier, {
          identifier: identifier,
          sellerType: sellerType,
          displayName,
          image1,
          image2,
          total: 0,
          totalProduct: 0,
          totalAdjustment: 0,
          totalShipping: 0,
          baskets: [basket],
        });
      } else {
        existing.baskets.push(basket);
      }
    }

    // Calculate totals for each seller group
    for (const group of groups.values()) {
      for (const basket of group.baskets) {
        const subBaskets: SubBasket[] = Object.values(basket.subBaskets ?? {});
        for (const sub of subBaskets) {
          group.total += sub.total;
          group.totalProduct += sub.totalProduct;
          group.totalAdjustment += sub.totalAdjustment;
          group.totalShipping += sub.totalShipping;
        }
      }
    }

    return Array.from(groups.values());
  }
);

export const selectHasExternalSeller = createSelector(
  [selectAllBaskets],
  (baskets) =>
    baskets.some(
      (basket) =>
        basket.identifier?.sellerIdentifier?.sellerType === SellerType.External
    )
);

export const selectHasBaskets = createSelector(
  [selectAllBaskets],
  (baskets) => baskets.length > 0
);

export const selectFromBasketsExternalSellersOnly = createSelector(
  [selectAllBaskets],
  (baskets) =>
    baskets
      .flatMap((b) => b.identifier.sellerIdentifier)
      .filter((si): si is SellerIdentifier => si != null)
      .filter((si) => si.sellerType === "EXTERNAL")
      .flatMap((si) => si.identifier)
);

export const selectBasketValidations = createSelector(
  [selectAllBaskets],
  (baskets): BasketValidationType[] => {
    return baskets
      .filter(Boolean)
      .map((basket): BasketValidationType | undefined => {
        const { validation, identifier } = basket;

        if (validation == null || identifier.type === BASKET_TYPE.OPTAIN)
          return undefined;

        return {
          key: `validation_${createBasketKey(identifier)}`,
          validation,
        };
      })
      .filter((v): v is BasketValidationType => v !== undefined);
  }
);

export const selectSellerValidations = createSelector(
  [selectGroupedBasketsBySeller],
  (groupedSellers): Record<string, BasketValidationType[]> => {
    return groupedSellers.reduce(
      (acc, sellerGroup) => {
        const { identifier: sellerId, baskets } = sellerGroup;

        const validations = baskets
          .filter(
            (basket) =>
              basket.validation && basket.identifier.type !== BASKET_TYPE.OPTAIN
          )
          .map((basket) => ({
            key: `validation_${createBasketKey(basket.identifier)}`,
            validation: basket.validation!,
          }));

        if (validations.length > 0) {
          acc[sellerId] = validations;
        }

        return acc;
      },
      {} as Record<string, BasketValidationType[]>
    );
  }
);

export const selectAllSubBaskets = createSelector(
  [selectAllBaskets],
  (baskets) =>
    baskets.flatMap((basket) => {
      return basket.subBaskets;
    })
);

export const selectAllOrderItems = createSelector(
  [selectAllSubBaskets],
  (subBaskets) => {
    return subBaskets.flatMap((subBasket) => subBasket.orderitems);
  }
);

export const selectAllOrderItemsSorted = createSelector(
  [selectAllOrderItems],
  (orderItems) => {
    return [...orderItems].sort((orderItem1, orderItem2) => {
      return new Date(orderItem1.createDate).getTime() <
        new Date(orderItem2.createDate).getTime()
        ? 1
        : -1;
    });
  }
);

// create selector to filter order items by partnumber and free gift flag
export const makeSelectOrderItemByPartNumberAndFreeGift = (
  partNumber: string,
  isFreeGift: boolean
) => {
  const selectOrderItemByPartNumberAndFreeGift = createSelector(
    [selectAllOrderItems],
    (orderItems) => {
      return orderItems
        .filter(
          (item) =>
            item?.article?.partNumber &&
            item.article.partNumber === partNumber &&
            item.isFreeGift === isFreeGift
        )
        .find(Boolean);
    }
  );

  return selectOrderItemByPartNumberAndFreeGift;
};

export const selectOrderItemsWithPendingAvailability = createSelector(
  [selectAllOrderItems],
  (orderItems) =>
    orderItems.filter((orderItem) => orderItem?.availability?.pending)
);

export const selectPartNumbersOfAllOrderItems = createSelector(
  [selectAllOrderItems],
  (orderItems) =>
    orderItems.map((orderItem) => orderItem.article.eFoodPartnumber)
);

export const hasOrderErrorSelector = createSelector(
  [
    efoodSelector,
    selectAllBaskets,
    orderApprovalsSelector,
    promotionCodeSelector,
  ],
  ({ dropShipping }, baskets, approvals, promotionCode) => {
    const dropShippingCommentRequired = dropShipping
      .filter((item) => item.commentRequired === true)
      .map((result) => result.type);

    if (
      baskets.some(
        (basket) =>
          (basket.identifier.type === BASKET_TYPE.STOCK_OPTAIN &&
            basket.validation &&
            basket.validation.type === "DENIAL") ||
          (basket.identifier.type !== BASKET_TYPE.MARKETPLACE &&
            !basket.identifier.deliveryDate) ||
          (basket.identifier.dropShipType &&
            dropShippingCommentRequired.indexOf(
              basket.identifier.dropShipType
            ) > -1 &&
            (basket.comment === "" || basket.comment === null))
      ) ||
      approvals.some(
        (approval) =>
          approval.basketIdentifier.type === BASKET_TYPE.STOCK_OPTAIN &&
          approval.approvalStatus === "Denial"
      ) ||
      promotionCode.some((promotionCode) => !promotionCode.active)
    ) {
      return true;
    }

    return false;
  }
);

const DEFAULT_CHECKED_SELLERS = {};
export const selectCheckedSellersSelector = (state: RootReducerState): CartSelection["checkedSellers"] =>
  state.order.cartSelection?.checkedSellers ?? DEFAULT_CHECKED_SELLERS;

const DEFAULT_CHECKED_TOTALS = {
  total: 0,
  totalProduct: 0,
  totalAdjustment: 0,
  totalShipping: 0,
};
export const selectCheckedTotalsSelector = (state: RootReducerState): CartSelection["checkedTotals"] =>
  state.order.cartSelection?.checkedTotals ?? DEFAULT_CHECKED_TOTALS;
